<template>
  <b-modal
    id="user-wizard-modal"
    v-model="isWizardActive"
    title="User Wizard"
    hide-header
    centered
    no-close-on-esc
    no-close-on-backdrop
    content-class="modal-user"
    :size="modalSize"
    @shown="onShown"
    @hidden="resetAll"
  >
    <h4 class="mb-1 text-capitalize text-center py-1 title-user-wizard">
      {{ (isAdd ? $t("actions.add") : $t("actions.edit")) + " " + $tc("users.title", 1) }}
    </h4>

    <WizardStepIndicator
      :currentStep="currentStep"
      :numberedSteps="numberedSteps"
      :stepIcons="stepIcons"
      @step-clicked="handleStepClicked"
      :type-alarm-options="typeAlarmOptions"
      :is-add.sync="isAdd"
      :userGroup="userBelongGroup"
      :groupName="groupName"
    />

    <hr />

    <!-- <h5 class="modal-title">{{ numberedSteps[currentStep] }}</h5> -->
    <div class="b-modal-body" :style="modalStyle">
      <div v-if="loading" class="modal-loading">
        <b-spinner
          v-if="currentStep === 2 || loading"
          label="Spinning"
          variant="primary"
          class="modal-loading-spinning"
        ></b-spinner>
      </div>

      <GeneralInfoStep
        v-if="currentStepKey === 'general' && !loading"
        ref="generalInfoStepRef"
        :is-add.sync="isAdd"
        :prop-item-data="itemData"
        :country-options="countryOptions"
        :current-step="currentStep"
        @update:itemData="itemData = { ...itemData, ...$event }"
        :userGroup="userBelongGroup"
        :groupName="groupName"
      />

      <TenantStep
        v-if="currentStepKey === 'tenants' && !loading"
        ref="tenantStepRef"
        :prop-item-data="itemData"
        :current-step="currentStep"
        :master-tenant-options="masterTenantOptions"
        :type-alarm-options="typeAlarmOptions"
        :is-clone="isClone"
        @update:itemData="itemData = { ...itemData, ...$event }"
        :userGroup="userBelongGroup"
      />

      <PermissionStep
        v-if="currentStepKey === 'profile' && !loading"
        ref="permissionStepRef"
        :prop-item-data="itemData"
        :profile-options="profileOptions"
        @update:itemData="itemData = { ...itemData, ...$event }"
        :userGroup="userBelongGroup"
      />

      <!-- <CamerasStep
        v-if="currentStep === 3"
        :prop-user-email="userEmail"
        :prop-user-id="userId"
      /> -->

      <Device
        v-if="currentStepKey === 'devices'"
        :config="config"
        @updateStatus="updateStatus"
        :assingDevices="assingDevices"
        :is-parent-loading="parentLoading"
      />
    </div>

    <template #modal-footer>
      <div class="mr-auto">
        <button
          type="button"
          class="btn btn-danger text-capitalize"
          :disabled="loading"
          @click="closeModal"
        >
          {{ $t("actions.cancel") }}
        </button>
      </div>
      <div class="d-flex justify-content-between">
        <div class="d-flex">
          <button
            type="button"
            class="btn btn-info mr-2 text-capitalize"
            @click="save"
            :disabled="saveDisabled || loading"
            v-if="!isAdd && currentStepKey !== 'devices'"
            data-cy="automatest-wizard-save-btn"
          >
            {{ $t("actions.save_changes") }}
          </button>
          <button
            type="button"
            class="btn btn-secondary mr-2 text-capitalize"
            @click="currentStep--"
            :disabled="currentStep === 0 || loading"
          >
            {{ $t("actions.previous") }}
          </button>
          <button
            type="button"
            class="btn btn-primary text-capitalize"
            @click="nextStep"
            v-if="currentStep !== numberedSteps.length - 1 && !isLastGroupStep"
            :disabled="currentStep === numberedSteps.length - 1 || loading"
            data-cy="automatest-wizard-next-btn"
          >
            {{ $t("actions.next") }}
          </button>
          <button
            type="button"
            class="btn btn-primary text-capitalize"
            @click="closeModal"
            v-if="currentStep === numberedSteps.length - 1 || isLastGroupStep"
            :disabled="loading"
            data-cy="automatest-wizard-finish-btn"
          >
            {{ $t("actions.finish") }}
          </button>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { BModal, BSpinner } from "bootstrap-vue";
import WizardStepIndicator from "./WizardStepIndicator.vue";
import GeneralInfoStep from "./GeneralInfoStep.vue";
import TenantStep from "./TenantStep.vue";
import PermissionStep from "./PermissionStep.vue";
import CamerasStep from "@/views/user_cameras/Index.vue";
import store from "@/store";
import { ref, computed, watch, nextTick, onMounted } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import Device from "@/components/DeviceAssignment/Index.vue";
import ability from "@/libs/acl/ability";

export default {
  components: {
    BModal,
    BSpinner,
    WizardStepIndicator,
    TenantStep,
    GeneralInfoStep,
    PermissionStep,
    CamerasStep,
    Device,
  },
  props: {
    isWizardActive: {
      type: Boolean,
      required: true,
    },
    countryOptions: {
      type: Array,
      required: true,
    },
    masterTenantOptions: {
      type: Array,
      required: true,
    },
    typeAlarmOptions: {
      type: Array,
      required: true,
    },
    profileOptions: {
      type: Array,
      required: true,
    },
    isAdd: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
    },
    isClone: {
      type: Boolean,
      required: false,
      default: false,
    },
    itemCloned: {
      type: Object,
      required: false,
    },
    idItemOriginal: {
      type: Number,
      required: false,
    },
  },
  setup(props, { emit, parent }) {
    const currentStep = ref(0);
    const showAllUserSteps = ref(false);
    const userBelongGroup = ref(false);
    const groupName = ref('');
    const activeSteps = computed(() => [
      {
        key: "general",
        label: parent.$i18n.t("users.info"),
        icon: "UserIcon",
        visible: showAllUserSteps.value || ability.can("update_general_info", "User"),
      },
      {
        key: "tenants",
        label: parent.$i18n.tc("tenants.title", 2),
        icon: "UsersIcon",
        visible: showAllUserSteps.value || ability.can("update_tenants", "User"),
      },
      {
        key: "profile",
        label: parent.$i18n.tc("permissions.title", 2),
        icon: "CheckCircleIcon",
        visible: showAllUserSteps.value || ability.can("update_profile", "User"),
      },
      {
        key: "devices",
        label: parent.$i18n.tc("camera.title", 2),
        icon: "VideoIcon",
        visible: showAllUserSteps.value || ability.can("update_cameras", "User"),
      },
    ].filter(step => step.visible));
    const numberedSteps = computed(() => activeSteps.value.map(step => step.label));
    const stepIcons = computed(() => activeSteps.value.map(step => step.icon));
    const currentStepKey = computed(() => activeSteps.value[currentStep.value]?.key);
    const isLastGroupStep = computed(() =>
      userBelongGroup.value && currentStep.value === activeSteps.value.length - 2
    );
    const loading = ref(false);
    const generalInfoStepRef = ref(null);
    const tenantStepRef = ref(null);
    const permissionStepRef = ref(null);
    const userId = ref(null);
    const userEmail = ref(null);
    const saveDisabled = ref(true);
    const firstItemDataOnEdit = ref(null);
    const isInitialLoadComplete = ref(false);
    const parentLoading = ref(false);
    const originalDevices = ref([]);
    const result = ref();

    const blankData = {
      name: "",
      first_name: "",
      second_name: "",
      phone_number: "",
      plate_number: "",
      allowed_tabs: 5,
      email: "",
      password: "",
      master_tenant: null,
      tenant: [],
      id_profile: null,
      permission: null,
      type_alarm: null,
      active: 1,
      vpn_access: 0,
      allowed_units_live_video_flows: 1000,
      alarm_history_per_hours: 0,
      alarm_history_per_days: 0,
      alarm_history_by_records: 0,
      video_wall: 0,
      enabled_chat: 0,
      enabled_helpdesk: 0,
      id_country: 186,
      app_modules: []
    };

    const toast = useToast();
    const itemData = ref(JSON.parse(JSON.stringify(blankData)));
    const resetData = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData));
    };

    const loadData = async () => {
      if (!props.isAdd) {
        itemData.value = JSON.parse(JSON.stringify(props.itemEdit));
        itemData.value.master_tenant = props.itemEdit.master_tenants.map(
          (master_tenant) => {
            return master_tenant.id;
          }
        );
        itemData.value.tenant = props.itemEdit.tenants
          .filter((tenant) => tenant.pivot.owner == 1)
          .map((tenant) => {
            return tenant.id;
          });
          
        itemData.value.type_alarm = props.itemEdit.types_alarms.map((type_alarm) => {
          return type_alarm.id;
        });
        itemData.value.app_modules = props.itemEdit.app_modules.map((app_modules) => {
          return app_modules.id;
        });

        userId.value = props.itemEdit.id;
        userEmail.value = props.itemEdit.email;

        firstItemDataOnEdit.value = JSON.parse(JSON.stringify(itemData.value));
        isInitialLoadComplete.value = true;
        //Variables to disable Tenants, Permissions and Cameras if the user belongs to a group
        userBelongGroup.value = itemData.value?.groups[0] ? true : false;
        groupName.value = userBelongGroup.value ? itemData.value.groups[0].name : '';
      } else if (props.isAdd && props.isClone && props.itemCloned) {
        itemData.value = props.itemCloned;
        itemData.value.auth_two_factor_code = "";
        itemData.value.created = "";
        itemData.value.email = "";
        itemData.value.first_name = "";
        itemData.value.second_name = "";
        itemData.value.last_name = "";
        itemData.value.name = "";
        itemData.value.password = "";
        itemData.value.phone_number = "";
        itemData.value.phone_number_more_telephone_code = "";
        itemData.value.plate_number = "";
        itemData.value.active = 1;
        itemData.value.id_country = 186;

        itemData.value.master_tenant = props.itemCloned.master_tenants.map(
          (master_tenant) => {
            return master_tenant.id;
          }
        );
        itemData.value.tenant = props.itemCloned.tenants
          .filter((tenant) => tenant.pivot.owner == 1)
          .map((tenant) => {
            return tenant.id;
          });
        itemData.value.permission = props.itemCloned.permissions.map((permission) => {
          return permission.id;
        });
        itemData.value.type_alarm = props.itemCloned.types_alarms.map((type_alarm) => {
          return type_alarm.id;
        });

        itemData.value.app_modules = props.itemCloned.app_modules.map((app_modules) => {
          return app_modules.id;
        });

        await fetchOriginalDevices(props.idItemOriginal);
      } else {
        itemData.value.id_country = props.countryOptions
          ? props.countryOptions[0].value
          : 1;
      }
      setTimeout(setUpWatch, 5000);
    };

    const fetchOriginalDevices = async (userId) => {
      if (!userId) return;

      const params = {
        user: userId,
        assign: 1,
        action: "justIds",
      };
      await store.dispatch("camera_proxy/getAll", params);
      originalDevices.value = store.getters["camera_proxy/getAssignedIdsItems"];
      console.log("originalDevices", originalDevices.value);
    };

    const setUpWatch = () => {
      watch(
        () => JSON.parse(JSON.stringify(itemData.value)),
        (newVal, oldVal) => {
          if (isInitialLoadComplete.value) {
            const original = JSON.stringify(firstItemDataOnEdit.value);
            const updated = JSON.stringify(newVal);
            if (original !== updated) {
              saveDisabled.value = false;
            } else {
              saveDisabled.value = true;
            }
          }
        },
        { deep: true, immediate: true }
      );
    };

    const nextStep = async () => {
      let isValid = true;
      if (
        currentStepKey.value === "general" &&
        generalInfoStepRef.value &&
        generalInfoStepRef.value.validateStep
      ) {
        isValid = await generalInfoStepRef.value.validateStep();
      } else if (
        currentStepKey.value === "tenants" &&
        tenantStepRef.value &&
        tenantStepRef.value.validateStep
      ) {
        isValid = await tenantStepRef.value.validateStep();
      } else if (
        currentStepKey.value === "profile" &&
        permissionStepRef.value &&
        permissionStepRef.value.validateStep
      ) {
        isValid = await permissionStepRef.value.validateStep();
      }

      const nextStepData = activeSteps.value[currentStep.value + 1];

      // When the next visible step is device assignment, persist the user first.
      if (isValid && nextStepData?.key === "devices") {
        result.value = await submit();
        if (!result.value) return;

        loading.value = true;
        await store.dispatch("camera_proxy/getAll", {
          user: effectiveUserId.value,
          assign: 1,
          action: "justIds",
        });
        loading.value = false;
        currentStep.value++;
        return;
      }

      if (isValid) {
        currentStep.value++;
      }
    };

    const handleStepClicked = async (step) => {
      if (activeSteps.value[step]?.key === "devices") {
        loading.value = true;
        let params = {
          user: effectiveUserId.value,
          assign: 1,
          action: "justIds",
        };

        await store.dispatch("camera_proxy/getAll", params);
        loading.value = false;
      }

      currentStep.value = step;
    };

    const onShown = () => {
      showAllUserSteps.value = props.isAdd;
      currentStep.value = 0;
      loadData();
    };

    const save = async () => {
      let generalInfoStepValid = true;
      let tenantsStepValid = true;
      let permisionStepValid = true;

      if (generalInfoStepRef.value) {
        generalInfoStepValid = await generalInfoStepRef.value.validateStep();
      }

      if (tenantStepRef.value) {
        tenantsStepValid = await tenantStepRef.value.validateStep();
      }

      if (permissionStepRef.value) {
        permisionStepValid = await permissionStepRef.value.validateStep();
      }

      if (generalInfoStepValid && tenantsStepValid && permisionStepValid) {
        submit();
      }
    };

    const submit = async () => {
      let response;
      loading.value = true;

      toast({
        component: ToastificationContent,
        props: {
          title: parent.$i18n.t("users.saving_info"),
          icon: "InfoIcon",
          variant: "info",
        },
      });

      try {
        if (props.isAdd) {
          response = await store.dispatch("user/add", itemData.value);
          if (response.success && props.isClone) {
            userId.value = response.data.id; // Actualiza con el id del usuario clonado

            // Asigna las cámaras copiadas al usuario clonado
            await updateStatus({
              multiple: true,
              ids: originalDevices.value.cameraProxyIds,
              status: "1",
              id_user: userId.value,
            });
          }
        } else {
          const req = {
            id: itemData.value.id,
            params: {
              name: itemData.value.name,
              last_name: itemData.value.last_name,
              phone_number: itemData.value.phone_number,
              plate_number: itemData.value.plate_number,
              allowed_tabs: itemData.value.allowed_tabs,
              email: itemData.value.email,
              username: itemData.value.username,
              alarm_history_per_hours: itemData.value.alarm_history_per_hours,
              alarm_history_per_days: itemData.value.alarm_history_per_days,
              alarm_history_by_records: itemData.value.alarm_history_by_records,
              allowed_units_live_video_flows:
                itemData.value.allowed_units_live_video_flows,
              master_tenant: itemData.value.master_tenant,
              tenant: itemData.value.tenant,
              id_profile: itemData.value.id_profile,
              type_alarm: itemData.value.type_alarm,
              active: itemData.value.active,
              vpn_access: itemData.value.vpn_access,
              video_wall: itemData.value.video_wall,
              enabled_chat: itemData.value.enabled_chat,
              enabled_helpdesk: itemData.value.enabled_helpdesk,
              id_country: itemData.value.id_country,
              id_unit: itemData.value.id_unit,
              app_modules: itemData.value.app_modules
            },
          };
          response = await store.dispatch("user/edit", req);
        }
        if (response.success) {
          toast({
            component: ToastificationContent,
            props: {
              title: response.message,
              icon: "CheckIcon",
              variant: "success",
            },
          });
          if (props.isAdd) {
            userId.value = response.data.id;
            userEmail.value = response.data.email;
            itemData.value.id = response.data.id;
            emit("isAdd-false");
          }
          emit("refetch-data");
          loading.value = false;
          return true;
        } else {
          toast({
            component: ToastificationContent,
            props: {
              title: response.message,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
          loading.value = false;
          return false;
        }
      } catch (response) {
        if (response.response.status == 422) {
          let string = ``;
          Object.entries(response.response.data.data).forEach((entry) => {
            const [key, value] = entry;
            string += `${key} - ${value}<br>`;
          });
          toast(
            {
              component: ToastificationContent,
              props: {
                title: `${response.response.data.message}`,
                icon: "AlertTriangleIcon",
                variant: "danger",
                text: string,
              },
            },
            {
              timeout: 10000,
            }
          );
        } else {
          toast({
            component: ToastificationContent,
            props: {
              title: response.response.data.message,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        }
        loading.value = false;
        return false;
      }
    };

    const closeModal = () => {
      emit("close-wizard");
    };

    const resetAll = () => {
      resetData();
      currentStep.value = 0;
    };

    //set this variable to avoid send null to camera assignment
    const effectiveUserId = computed(
      () => userId.value ?? itemData.value?.id ?? props.itemEdit?.id
    );

    const config = {
      id_user: effectiveUserId,
      filters: {
        tenant: true,
        center: true,
        sector: true,
        units: true,
        online: false,
        assing: false,
      },
    };

    const assingDevices = computed(() => {
      return props.isClone
        ? originalDevices.value
        : store.getters["camera_proxy/getAssignedIdsItems"];
    });

    const updateStatus = async (data) => {
      parentLoading.value = true;

      // Si es múltiple y no se han pasado IDs, obtener todos los IDs usando filtros
      if (data.multiple && !data.ids) {
        const params_get_all = {
          q: data.searchQuery,
          units: data.id_unit,
          id_sector: data.id_sector,
          id_center: data.id_center,
          online: data.online,
          tenant: data.id_tenant,
          action: "justIds",
          id_user: effectiveUserId.value,
        };

        const response = await store.dispatch("camera_proxy/getAll", params_get_all);
        data.ids = response.data.cameraProxyIds; // Asegúrate de que esto sea un array plano
      }

      // Crear el payload correctamente con un array plano
      const params = {
        id_user: effectiveUserId.value,
        cameraProxyIds: Array.isArray(data.ids) ? data.ids : [data.id],
        status: data.status,
      };

      try {
        await store.dispatch("user/assignCameras", params);
        toast({
          component: ToastificationContent,
          props: {
            title: "Dispositivos asignados correctamente",
            icon: "CheckIcon",
            variant: "success",
          },
        });

        await store.dispatch("camera_proxy/getAll", {
          user: effectiveUserId.value,
          action: "justIds",
          assign: 1,
        });
      } catch (error) {
        console.error("Error en la asignación de dispositivos:", error);
        toast({
          component: ToastificationContent,
          props: {
            title: "Error en la asignación de dispositivos",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        parentLoading.value = false;
      }
    };

    const modalSize = computed(() => {
      return currentStepKey.value === "devices" ? "xl" : "lg";
    });

    const modalStyle = computed(() => {
      return {
        height: "60vh",
        overflowY: "auto",
      };
    });

    return {
      currentStep,
      numberedSteps,
      stepIcons,
      currentStepKey,
      isLastGroupStep,
      itemData,
      userId,
      userEmail,
      originalDevices,
      generalInfoStepRef,
      tenantStepRef,
      permissionStepRef,
      loadData,
      resetData,
      nextStep,
      handleStepClicked,
      submit,
      loading,
      closeModal,
      resetAll,
      config,
      assingDevices,
      updateStatus,
      save,
      saveDisabled,
      parentLoading,
      onShown,
      modalSize,
      modalStyle,
      userBelongGroup,
      groupName,
    };
  },
};
</script>

<style scoped>
.close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  font-size: 1.5rem;
  cursor: pointer;
}

.div-container {
  max-width: 80%;
  margin: auto;
}
</style>

<style scoped>
.modal-user .modal-body {
  padding: 0 !important;
}
</style>
